// Curated, per-grass seed picks for the grass-comparison tool. Unlike the seeding
// calculator (one result grass -> one seed), the comparison tool has 2-3 selected
// grasses, so this returns one buy column per seedable grass plus a single shared
// starter-fertilizer companion.
//
// Reads the same shared grass -> seed map the seeding calculator uses
// (lib/product-picks/seed-by-grass.ts), so coverage never drifts between surfaces.
// Grasses with no seed (St. Augustine — sod/plugs only) are skipped, and if none of
// the selected grasses are seed-viable the list is empty so the caller renders
// nothing rather than an empty card.

import type { ProductRecommendationLink } from '@/lib/product-picks/types'
import { linkFromCatalog } from '@/lib/product-picks/resolve'
import { SEED_BY_GRASS, STARTER, normalizeGrass } from '@/lib/product-picks/seed-by-grass'

export interface ComparisonSeedColumn {
  /** Original selected-grass display name. */
  grass: string
  pick: ProductRecommendationLink
  isChampion: boolean
}

export interface ComparisonSeedPicks {
  /** Seed card columns, champion-first when a champion is known. */
  columns: ComparisonSeedColumn[]
  /** Shared starter companion, shown as a footer row when columns exist. */
  starter: ProductRecommendationLink | null
  /** Selected grasses with no seed (e.g. St. Augustine — sod/plugs only). */
  skippedGrasses: string[]
}

/**
 * Curated seed picks matched to the user's selected grasses (one per seed-viable
 * grass), plus a single starter-fertilizer companion. Reports skipped grasses so
 * the caller can label the block honestly (a St. Augustine + Bermuda comparison
 * shows only Bermuda seed, so the copy must not claim it covers "each grass").
 * Pure.
 */
export function getComparisonSeedPicks(grasses: string[], championName: string | null): ComparisonSeedPicks {
  const championKey = championName ? normalizeGrass(championName) : null
  const columnsWithIndex: Array<ComparisonSeedColumn & { index: number }> = []
  const skippedGrasses: string[] = []

  grasses.forEach((grass, index) => {
    const normalized = normalizeGrass(grass)
    const pick = SEED_BY_GRASS[normalized]
    if (!pick) {
      skippedGrasses.push(grass)
      return
    }

    const link = linkFromCatalog(pick.key, pick.reason)
    if (!link) {
      skippedGrasses.push(grass)
      return
    }

    columnsWithIndex.push({
      grass,
      pick: link,
      isChampion: championKey !== null && normalized === championKey,
      index,
    })
  })

  if (columnsWithIndex.length === 0) return { columns: [], starter: null, skippedGrasses }

  const columns = columnsWithIndex
    .sort((a, b) => Number(b.isChampion) - Number(a.isChampion) || a.index - b.index)
    .map(({ index: _index, ...column }) => column)

  return {
    columns,
    starter: linkFromCatalog(STARTER.key, STARTER.reason),
    skippedGrasses,
  }
}
